<template>
  <registry-page
    title="Шаблоны проводок"
    description="Правила формирования проводок из бухгалтерских документов."
  >
    <template #actions>
      <q-btn
        v-if="canEditFinance"
        color="primary"
        icon="add"
        no-caps
        label="Новый шаблон"
        data-testid="template-new"
        :to="{ name: 'transfer_templates_new' }"
      />
    </template>

    <app-data-table
      title="Шаблоны"
      :count="filteredRows.length"
      :rows="filteredRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="stateMessage"
      storage-key="transfer-templates"
      data-testid="transfer-templates-table"
      @retry="loadTemplates"
      @reset="resetFilters"
    >
      <template #filters>
        <filter-bar
          v-model="search"
          search-label="Название, тип или счет"
          search-test-id="template-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="syncQuery(false)"
          @reset="resetFilters"
          @remove="removeFilter"
        >
          <template #quick>
            <q-select
              v-model="statusFilter"
              dense outlined emit-value map-options
              label="Статус"
              :options="statusOptions"
              class="transfer-template-filter"
              @update:model-value="syncQuery(true)"
            />
          </template>
          <template #advanced>
            <q-select
              v-model="statusFilter"
              dense outlined emit-value map-options
              label="Статус"
              :options="statusOptions"
              @update:model-value="syncQuery(true)"
            />
          </template>
        </filter-bar>
      </template>
      <template #body-cell-status="props">
        <q-td :props="props">
          <status-badge
            :label="props.row.is_active ? 'Активен' : 'В архиве'"
            :tone="props.row.is_active ? 'success' : 'neutral'"
            :icon="props.row.is_active ? 'check_circle' : 'archive'"
          />
        </q-td>
      </template>
      <template #body-cell-lines="props">
        <q-td :props="props">{{ props.row.lines.length }}</q-td>
      </template>
      <template #body-cell-accounts="props">
        <q-td :props="props">
          <span v-if="props.row.lines.length">
            {{ props.row.lines[0].debit_account || '—' }} / {{ props.row.lines[0].credit_account || '—' }}
          </span>
          <span v-else>—</span>
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props">
          <q-btn
            v-if="canEditFinance && props.row.is_active"
            flat round dense icon="edit"
            :aria-label="`Редактировать ${props.row.name}`"
            :to="{ name: 'transfer_templates_edit', params: { id: props.row.id } }"
          />
          <q-btn
            v-if="canEditFinance && props.row.is_active"
            flat round dense icon="archive" color="negative"
            :aria-label="`Архивировать ${props.row.name}`"
            @click="archiveTemplate(props.row)"
          />
        </q-td>
      </template>
    </app-data-table>
  </registry-page>
</template>

<script>
import AppDataTable from '../../../components/ui/AppDataTable.vue'
import FilterBar from '../../../components/ui/FilterBar.vue'
import RegistryPage from '../../../components/ui/RegistryPage.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'
import { normalizeTransferTemplateList } from '../../../services/transferWorkspace'

const columns = Object.freeze([
  { name: 'name', align: 'left', label: 'Название', field: 'name', required: true, sortable: true },
  { name: 'document_type', align: 'left', label: 'Тип документа', field: (row) => row.document_type_display || row.document_type || '—', sortable: true },
  { name: 'direction', align: 'left', label: 'Направление', field: (row) => row.direction_display || row.direction || '—', classes: 'column-priority-secondary' },
  { name: 'accounts', align: 'left', label: 'Дебет / кредит', field: 'accounts', classes: 'column-priority-secondary' },
  { name: 'lines', align: 'right', label: 'Строк', field: (row) => row.lines.length, sortable: true },
  { name: 'version', align: 'right', label: 'Версия', field: (row) => row.version || '—', classes: 'column-priority-secondary' },
  { name: 'status', align: 'left', label: 'Статус', field: 'is_active' },
  { name: 'actions', align: 'right', label: 'Действия', field: 'id', required: true }
])

export default {
  name: 'transfer_templates_list',
  components: { AppDataTable, FilterBar, RegistryPage, StatusBadge },
  data() {
    return {
      columns,
      rows: [],
      search: String(this.$route.query.search || ''),
      statusFilter: String(this.$route.query.status || 'active'),
      statusOptions: [
        { label: 'Активные', value: 'active' },
        { label: 'Архивные', value: 'archived' },
        { label: 'Все', value: 'all' }
      ],
      pagination: { page: 1, rowsPerPage: 25, sortBy: 'name', descending: false },
      loading: true,
      error: '',
      errorState: 'error',
      handlers: []
    }
  },
  computed: {
    canEditFinance() {
      return this.$store.state.permissions.edit_fin_documents === true
    },
    hasFilters() {
      return Boolean(this.search || this.statusFilter !== 'active')
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.statusFilter !== 'active') {
        const option = this.statusOptions.find((item) => item.value === this.statusFilter)
        filters.push({ key: 'status', label: `Статус: ${option ? option.label : this.statusFilter}` })
      }
      return filters
    },
    filteredRows() {
      const query = this.search.trim().toLocaleLowerCase('ru-RU')
      return this.rows.filter((row) => {
        const matchesStatus = this.statusFilter === 'all' || (this.statusFilter === 'active' ? row.is_active : !row.is_active)
        const lineText = row.lines.map((line) => `${line.debit_account || ''} ${line.credit_account || ''}`).join(' ')
        const matchesSearch = !query || [row.name, row.document_type_display, row.direction_display, lineText]
          .some((value) => String(value || '').toLocaleLowerCase('ru-RU').includes(query))
        return matchesStatus && matchesSearch
      })
    },
    tableState() {
      if (this.loading) return 'loading'
      if (this.error) return this.errorState
      if (!this.rows.length) return 'empty'
      if (!this.filteredRows.length) return 'filtered-empty'
      return 'ready'
    },
    stateTitle() {
      if (this.error) return 'Не удалось загрузить шаблоны'
      return this.hasFilters ? 'Ничего не найдено' : 'Шаблонов пока нет'
    },
    stateMessage() {
      return this.error || (this.hasFilters ? 'Измените или сбросьте фильтры.' : 'Создайте первый шаблон проводки.')
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        this.search = String(query.search || '')
        this.statusFilter = String(query.status || 'active')
      }
    }
  },
  mounted() {
    this.listen('onReceiveTransferTemplateList', this.onTemplates)
    this.listen('onArchiveTransferTemplate', this.onArchived)
    this.listen('connectionReady', this.loadTemplates)
    if (this.connection.isAuthenticated) this.loadTemplates()
  },
  beforeUnmount() {
    this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler))
  },
  methods: {
    listen(event, handler) {
      this.emitter.on(event, handler)
      this.handlers.push([event, handler])
    },
    loadTemplates() {
      this.loading = true
      this.error = ''
      this.connection.listTransferTemplates('transfer-templates-list')
    },
    onTemplates(response) {
      this.loading = false
      const normalized = normalizeTransferTemplateList(response)
      if (!normalized.ok) {
        this.errorState = normalized.state || 'error'
        this.error = normalized.message
        return
      }
      this.rows = normalized.rows
    },
    archiveTemplate(template) {
      this.$q.dialog({
        title: 'Архивация шаблона',
        message: `Архивировать «${template.name}»?`,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.loading = true
        this.connection.archiveTransferTemplate('transfer-template-archive', template.id)
      })
    },
    onArchived(response) {
      if (response.type === 'error' || response.error) {
        this.loading = false
        this.error = response.message || 'Не удалось архивировать шаблон'
        return
      }
      this.loadTemplates()
    },
    syncQuery(push = false) {
      this.$router[push ? 'push' : 'replace']({
        query: {
          ...(this.search ? { search: this.search } : {}),
          ...(this.statusFilter !== 'active' ? { status: this.statusFilter } : {})
        }
      })
    },
    resetFilters() {
      this.search = ''
      this.statusFilter = 'active'
      this.syncQuery(true)
    },
    removeFilter(key) {
      if (key === 'search') this.search = ''
      if (key === 'status') this.statusFilter = 'active'
      this.syncQuery(true)
    }
  }
}
</script>

<style scoped>
.transfer-template-filter { min-width: 10rem; }
</style>
